import { computedFrom, observable } from "aurelia-binding";
import { autoinject, transient } from "aurelia-dependency-injection";
import * as Enumerable from "linq";
import { EntityTypeConfigurationHelper, EntityTypeConfigurationModel } from "../../../../../../models/basics/basics.models";
import { LookupValue } from "../../../../../../models/lookups/lookup.models";
import { RightShipInspectionModel, RightShipInspectionType } from "../../../../../../models/rightship-inspections/rightship-inspections.models";
import { UserClientService } from "../../../../../../services/authentication/user-client.service";
import { Reflection } from "../../../../../../utilities/reflection";
import { EventAggregator } from "aurelia-event-aggregator";
import { Messages } from "../../../../../../configuration/event-messages.constants";

@autoinject
@transient()
export class VesselDetailsTab {

    @observable()
    vessel: {
        vesselId: number,
        hullTypeId?: number,
        commericalOwnerCompany?: string,
        name: string,
        internationalMaritimeOrganizationNumber: string,
        dwt?: number,
        flagId?: number,
        dateOfBuild?: Date,
        vesselTypeId?: number,
        vesselType?: string,
        beneficialOwnerCompanyId?: number,
        beneficialOwnerCompany?: string,
        isSanctioned?: boolean,
        isFlagFake?: boolean,
        isRestricted?: boolean,
        isDOCRestricted?: boolean,
        isBORestricted?: boolean
    };
    vesselEntityTypes: EntityTypeConfigurationModel[];
    isAuthenticated: boolean = false;
    showCargoOperationComments: boolean = false;
    item: RightShipInspectionModel;
    showVesselEF: boolean = false;
    public vesselIsRestricted: boolean;
    public vesselHasBeneficialOwnerMissing: boolean;
    public companyIsRestricted: boolean;

    constructor(private entityTypeConfigurationHelper: EntityTypeConfigurationHelper, userClientService: UserClientService, private eventAggregator: EventAggregator) {
        this.isAuthenticated = userClientService.currentUser && userClientService.currentUser.id > 0;
        this.vesselEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Vessels"]);
    }

    activate(item: RightShipInspectionModel) {
        this.item = item;
        this.companyIsRestricted = this.vessel?.isDOCRestricted || this.vessel?.isBORestricted;
    }
    
    //On vessel selected
    public onVesselSelected(vessel) {
        if (this.item) {
            this.item.vesselId = vessel ? parseInt(vessel.id) : null;
            this.vessel = vessel ? {
                vesselId: vessel.id,
                name: vessel.name,
                dwt: vessel.dwt,
                flagId: vessel.flagId,
                vesselTypeId: vessel.shipTypeId,
                vesselType: vessel.shipType,
                dateOfBuild: vessel.dateOfBuild,
                internationalMaritimeOrganizationNumber: vessel.internationalMaritimeOrganizationNumber,
                isSanctioned: vessel.isSanctioned,
                isFlagFake: vessel.isFlagFake,
                isRestricted: vessel.isRestricted,
                beneficialOwnerCompanyId: vessel.beneficialOwnerCompanyId,
                beneficialOwnerCompany: vessel.beneficialOwnerCompanyName,
                isDOCRestricted: vessel.isDOCRestricted,
                isBORestricted: vessel.isBORestricted
            } : null;

            //Check if vessel is restricted or has beneficial owner missing to show banner in the UI
            if(this.vessel) {
                this.vesselIsRestricted = this.vessel.isRestricted;                
                this.vesselHasBeneficialOwnerMissing = (!this.vessel.beneficialOwnerCompanyId || 
                    (this.vessel.beneficialOwnerCompany && this.vessel.beneficialOwnerCompany.toLowerCase() == "unknown"));
                this.companyIsRestricted = this.vessel.isDOCRestricted || this.vessel.isBORestricted;
                this.eventAggregator.publish(Messages.InspectedVesselChanged, {isDisabled: this.vesselIsRestricted || this.vesselHasBeneficialOwnerMissing || this.companyIsRestricted, vesselId: this.vessel.vesselId});
            }else{
                this.vesselIsRestricted = false;
                this.vesselHasBeneficialOwnerMissing = false;
                this.companyIsRestricted = false;
                this.eventAggregator.publish(Messages.InspectedVesselChanged, {isDisabled: this.vesselIsRestricted || this.vesselHasBeneficialOwnerMissing || this.companyIsRestricted, vesselId: null});
            }
        }
    }

    @computedFrom('vessel')
    public get vesselIdentifier() {
        if (!this.vessel) return "";
        const identifierKeys = ["internationalMaritimeOrganizationNumber", "imo", "europeanNumberOfIdentification", "eni", "vehicleIdentificationNumber", "vin"];
        const properties = Object.keys(this.vessel);
        const identifierProperty = Enumerable.from(identifierKeys).firstOrDefault(x => properties.indexOf(x) > -1);
        return this.vessel[identifierProperty];
    }

    @computedFrom(Reflection.getPropertyPath<VesselDetailsTab>(z => z.item),
        Reflection.getPropertyPath<VesselDetailsTab>(z => z.item.rightShipInspectionTypeId))
    get isWet() {
        return this.item && this.item.rightShipInspectionTypeId == RightShipInspectionType.TankerInspection;
    }

    @computedFrom(Reflection.getPropertyPath<VesselDetailsTab>(z => z.item),
        Reflection.getPropertyPath<VesselDetailsTab>(z => z.item.rightShipInspectionTypeId))
    get isCargill() {
        return this.item && this.item.rightShipInspectionTypeId == RightShipInspectionType.CargillSurvey;
    }

    @computedFrom(Reflection.getPropertyPath<VesselDetailsTab>(z => z.isWet),
        Reflection.getPropertyPath<VesselDetailsTab>(z => z.isCargill))
    get isWetOrCargill() {
        return this.isWet || this.isCargill;
    }

    customYesNo(prop: string, value: boolean) {
        if (prop == "isRoRoShip" && this.roroShipDisabled) {
            return;
        }
        else if (prop == "isTimberCarrier" && this.timberCarrierDisabled) {
            return;
        }
        else if (prop == "isCarryingGrainCargoAndHasBeenFumigatedInThePast12Months" && this.grainCargoFumigatedDisabled) {
            return;
        }
        else if (prop == "isSelfUnloadingVessel" && this.selfUnloadingDisabled) {
            return;
        }
        else {
            this.item[prop] = value;
        }
    }

    setRightShipInspectionVesselTypeId(id: number) {
        this.item.rightShipInspectionVesselTypeId = id;

        if (this.item.rightShipInspectionVesselTypeId == 1) {
            this.item.isRoRoShip = false;
            this.item.isTimberCarrier = false;
            this.item.isCarryingGrainCargoAndHasBeenFumigatedInThePast12Months = false;
        }
        else if (this.item.rightShipInspectionVesselTypeId == 2) {
            this.item.isRoRoShip = false;
            this.item.isTimberCarrier = false;
        }
        else if (this.item.rightShipInspectionVesselTypeId == 3) {
            this.item.isSelfUnloadingVessel = false;
            this.item.isCarryingGrainCargoAndHasBeenFumigatedInThePast12Months = false;
        }
        else if (this.item.rightShipInspectionVesselTypeId == 4) {
            this.item.isSelfUnloadingVessel = false;
            this.item.isRoRoShip = false;
            this.item.isTimberCarrier = false;
            this.item.isCarryingGrainCargoAndHasBeenFumigatedInThePast12Months = false;
        }
    }

    @computedFrom("item.rightShipInspectionVesselTypeId")
    get grainCargoFumigatedDisabled() {
        if (this.item.rightShipInspectionVesselTypeId == 1 ||
            this.item.rightShipInspectionVesselTypeId == 3 ||
            this.item.rightShipInspectionVesselTypeId == 4) {
            return true;
        }
        return false;
    }

    @computedFrom("item.rightShipInspectionVesselTypeId")
    get roroShipDisabled() {
        if (this.item.rightShipInspectionVesselTypeId == 1 ||
            this.item.rightShipInspectionVesselTypeId == 2 ||
            this.item.rightShipInspectionVesselTypeId == 4) {
            return true;
        }
        return false;
    }

    @computedFrom("item.rightShipInspectionVesselTypeId")
    get timberCarrierDisabled() {
        if (this.item.rightShipInspectionVesselTypeId == 1 ||
            this.item.rightShipInspectionVesselTypeId == 2 ||
            this.item.rightShipInspectionVesselTypeId == 4) {
            return true;
        }
        return false;
    }

    @computedFrom("item.rightShipInspectionVesselTypeId")
    get selfUnloadingDisabled() {
        if (this.item.rightShipInspectionVesselTypeId == 3 ||
            this.item.rightShipInspectionVesselTypeId == 4) {
            return true;
        }
        return false;
    }

    cargoOperationChanged(value: LookupValue) {
        if (value != null && value.additionalData != null && Object.keys(value.additionalData).indexOf('code') > -1) {
            //other OPeration
            this.showCargoOperationComments = value.additionalData['code'] == 5;
            if (!this.showCargoOperationComments)
                this.item.cargoOperationComments = null;
        }
    }
}